// "Quotes" card of a charter request: Add quote / Send quotes and the quotes table.
import type { NavigateFunction } from 'react-router-dom';
import { Button, Card, Popconfirm, Space, Table, Tooltip } from 'antd';
import { PlusOutlined, SendOutlined } from '@ant-design/icons';

import type { CharterQuote, CharterRequestDetail } from '../../api/types';
import { quoteColumns } from './quoteColumns';

const CANCELLED_REASON = 'This request is cancelled; it takes no new quotes.';

export function QuotesCard({
  r,
  isBooked,
  hasDrafts,
  openCreate,
  openEdit,
  sendMut,
  deleteMut,
  nav,
}: {
  r: CharterRequestDetail;
  isBooked: boolean;
  hasDrafts: boolean;
  openCreate: () => void;
  openEdit: (q: CharterQuote) => void;
  sendMut: { isPending: boolean; mutate: () => void };
  deleteMut: { mutate: (qid: string) => void };
  nav: NavigateFunction;
}) {
  const isCancelled = r.status === 'cancelled';
  const addQuoteBlocked = isCancelled
    ? CANCELLED_REASON
    : isBooked
      ? 'This request is booked; it takes no more quotes.'
      : undefined;

  return (
    <Card
      title="Quotes"
      extra={
        <Space>
          {/* PO, 29 Sep — a booked request takes no more quotes: the customer
              has already chosen one. Disabled with the reason, not hidden,
              so the admin is not left wondering where the button went.
              SHAR-1197 — nor does a cancelled one, and it is sent none. */}
          <Tooltip title={addQuoteBlocked}>
            <Button icon={<PlusOutlined />} onClick={openCreate} disabled={!!addQuoteBlocked}>Add quote</Button>
          </Tooltip>
          <Popconfirm
            title="Send quotes to the user?"
            description="Draft quotes will become visible in the mobile app."
            okText="Send"
            onConfirm={() => sendMut.mutate()}
            disabled={!hasDrafts || isCancelled}
          >
            <Tooltip title={isCancelled ? CANCELLED_REASON : undefined}>
              <Button type="primary" icon={<SendOutlined />} loading={sendMut.isPending} disabled={!hasDrafts || isCancelled}>
                Send quotes
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      }
    >
      {/* SHAR-608 — the AC lists sixteen fields per quote against the seven this
          table showed. With that many columns the table has to scroll
          horizontally and clip long values, the same treatment the requests
          list and the bookings list already use, rather than compressing every
          column until nothing is readable. */}
      <Table<CharterQuote>
        rowKey="id"
        dataSource={r.quotes}
        pagination={false}
        size="small"
        scroll={{ x: 2100 }}
        locale={{ emptyText: 'No quotes yet' }}
          columns={quoteColumns({ nav, openEdit, deleteMut })}
        // SHAR-1024 - no passenger section under a quote. Passengers belong to
        // the charter BOOKING and are managed on its own page; a quote is an
        // offer, and the request screen is not where its manifest is edited.
      />
    </Card>
  );
}
